import { useState, useEffect } from 'react'
import { AlertTriangle, Package, XCircle, CheckCircle, Search } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import { Giacenza, TIPI_ARTICOLO_LABELS, TipoArticolo } from '../../../types/magazzino'

type FilterStato = '' | 'sotto_scorta' | 'esaurito'

export default function GiacenzePage() {
  const [giacenze, setGiacenze] = useState<Giacenza[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStato, setFilterStato] = useState<FilterStato>('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadGiacenze()
  }, [])

  const loadGiacenze = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/magazzino/giacenze')
      setGiacenze(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const totaleArticoli = giacenze.length
  const sottoScorta = giacenze.filter(g => g.stato_scorta === 'sotto_scorta').length
  const esauriti = giacenze.filter(g => g.stato_scorta === 'esaurito').length

  const filtered = giacenze
    .filter(g => {
      const matchSearch = !search ||
        g.codice.toLowerCase().includes(search.toLowerCase()) ||
        g.descrizione.toLowerCase().includes(search.toLowerCase()) ||
        (g.fornitore?.toLowerCase() || '').includes(search.toLowerCase())
      const matchStato = !filterStato || g.stato_scorta === filterStato
      return matchSearch && matchStato
    })
    .sort((a, b) => {
      // Ordinamento: esaurito prima, poi sotto_scorta, poi ok
      const order = { esaurito: 0, sotto_scorta: 1, ok: 2 }
      return order[a.stato_scorta] - order[b.stato_scorta]
    })

  const TIPO_COLORS: Record<TipoArticolo, string> = {
    materia_prima: 'bg-amber-100 text-amber-700',
    semilavorato: 'bg-blue-100 text-blue-700',
    prodotto_finito: 'bg-green-100 text-green-700',
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Giacenze Magazzino</h1>
        <p className="text-gray-500">Panoramica scorte e allarmi</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <Package className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Articoli Attivi</p>
            <p className="text-2xl font-bold text-gray-900">{totaleArticoli}</p>
          </div>
        </div>
        <button
          onClick={() => setFilterStato(filterStato === 'sotto_scorta' ? '' : 'sotto_scorta')}
          className={`bg-white rounded-xl shadow-sm p-5 flex items-center gap-4 text-left transition-colors ${
            filterStato === 'sotto_scorta' ? 'ring-2 ring-amber-500' : ''
          }`}
        >
          <div className="p-3 bg-amber-100 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Sotto Scorta</p>
            <p className="text-2xl font-bold text-amber-600">{sottoScorta}</p>
          </div>
        </button>
        <button
          onClick={() => setFilterStato(filterStato === 'esaurito' ? '' : 'esaurito')}
          className={`bg-white rounded-xl shadow-sm p-5 flex items-center gap-4 text-left transition-colors ${
            filterStato === 'esaurito' ? 'ring-2 ring-red-500' : ''
          }`}
        >
          <div className="p-3 bg-red-100 rounded-lg">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Esauriti</p>
            <p className="text-2xl font-bold text-red-600">{esauriti}</p>
          </div>
        </button>
      </div>

      {/* Ricerca */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per codice, descrizione, fornitore..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {filterStato && (
            <button
              onClick={() => setFilterStato('')}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Mostra tutti
            </button>
          )}
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {filterStato ? 'Nessun articolo con questo stato' : 'Nessun articolo in magazzino'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Giacenza</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prenotato</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Disponibile</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scorta Min.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ubicazione</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fornitore</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map(g => {
                const rowBg = g.stato_scorta === 'esaurito' ? 'bg-red-50' :
                              g.stato_scorta === 'sotto_scorta' ? 'bg-amber-50' : ''
                return (
                  <tr key={g.id} className={`${rowBg} hover:bg-gray-50`}>
                    <td className="px-6 py-4">
                      {g.stato_scorta === 'ok' && (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs font-medium">OK</span>
                        </span>
                      )}
                      {g.stato_scorta === 'sotto_scorta' && (
                        <span className="inline-flex items-center gap-1 text-amber-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs font-medium">Sotto scorta</span>
                        </span>
                      )}
                      {g.stato_scorta === 'esaurito' && (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span className="text-xs font-medium">Esaurito</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-mono text-sm font-medium text-gray-900">{g.codice}</td>
                    <td className="px-6 py-4 text-gray-900">{g.descrizione}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[g.tipo]}`}>
                        {TIPI_ARTICOLO_LABELS[g.tipo]}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${
                      g.stato_scorta === 'esaurito' ? 'text-red-600' :
                      g.stato_scorta === 'sotto_scorta' ? 'text-amber-600' : 'text-gray-900'
                    }`}>
                      {g.giacenza_attuale} {g.unita_misura}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {g.prenotato > 0 ? (
                        <span className="font-medium text-blue-600">{g.prenotato} {g.unita_misura}</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${
                      g.giacenza_disponibile <= 0 ? 'text-red-600' :
                      g.giacenza_disponibile < g.scorta_minima ? 'text-amber-600' : 'text-gray-900'
                    }`}>
                      {g.giacenza_disponibile} {g.unita_misura}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      {g.scorta_minima} {g.unita_misura}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{g.ubicazione || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{g.fornitore || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
